import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Send, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTIONS = [
  "STAT / Emergency Delivery",
  "Scheduled Route",
  "Laboratory Specimens",
  "Pharmacy Delivery",
  "Medical Records",
  "Medical Equipment",
  "Other / Not Sure",
];

const EMPTY = {
  company: "",
  contact_name: "",
  phone: "",
  email: "",
  pickup_city: "",
  delivery_city: "",
  service_type: "",
  comments: "",
};

export default function QuoteForm() {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contact_name || !form.phone || !form.email || !form.service_type) {
      toast.error("Please fill in name, phone, email and service required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/quote`, form);
      setDone(true);
      setForm(EMPTY);
      toast.success("Request received. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please call us at 931-325-9599.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid="quote-success"
        className="border border-gray-200 bg-white p-10 text-center"
      >
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-[#0B2447]">
          <Send className="h-6 w-6 text-white" strokeWidth={1.5} />
        </div>
        <h3 className="font-heading text-2xl font-bold text-[#0B2447]">
          Request Received
        </h3>
        <p className="mt-3 text-[#4B5563]">
          Thank you. A BSC Rapid Med dispatcher will reach out shortly. For
          time-critical needs, call{" "}
          <a href="tel:9313259599" className="font-semibold text-[#D90429]">
            931-325-9599
          </a>
          .
        </p>
        <Button
          data-testid="quote-new-request"
          onClick={() => setDone(false)}
          className="mt-6 rounded-none bg-[#0B2447] hover:bg-[#081b36]"
        >
          Submit another request
        </Button>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="quote-form"
      className="border border-gray-200 bg-white p-6 sm:p-8"
    >
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Contact Name *" testid="qf-name">
          <Input
            data-testid="qf-name-input"
            value={form.contact_name}
            onChange={update("contact_name")}
            placeholder="Jane Doe"
            className="rounded-none border-gray-300"
          />
        </Field>
        <Field label="Company / Facility" testid="qf-company">
          <Input
            data-testid="qf-company-input"
            value={form.company}
            onChange={update("company")}
            placeholder="City Medical Lab"
            className="rounded-none border-gray-300"
          />
        </Field>
        <Field label="Phone *" testid="qf-phone">
          <Input
            data-testid="qf-phone-input"
            value={form.phone}
            onChange={update("phone")}
            placeholder="(931) 000-0000"
            className="rounded-none border-gray-300"
          />
        </Field>
        <Field label="Email *" testid="qf-email">
          <Input
            data-testid="qf-email-input"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@facility.com"
            className="rounded-none border-gray-300"
          />
        </Field>
        <Field label="Pickup City" testid="qf-pickup">
          <Input
            data-testid="qf-pickup-input"
            value={form.pickup_city}
            onChange={update("pickup_city")}
            placeholder="Columbia"
            className="rounded-none border-gray-300"
          />
        </Field>
        <Field label="Delivery City" testid="qf-delivery">
          <Input
            data-testid="qf-delivery-input"
            value={form.delivery_city}
            onChange={update("delivery_city")}
            placeholder="Nashville"
            className="rounded-none border-gray-300"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Service Required *" testid="qf-service">
            <Select
              value={form.service_type}
              onValueChange={update("service_type")}
            >
              <SelectTrigger
                data-testid="qf-service-trigger"
                className="rounded-none border-gray-300"
              >
                <SelectValue placeholder="Select a service" />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`qf-service-${s}`}>
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <div className="sm:col-span-2">
          <Field label="Comments" testid="qf-comments">
            <Textarea
              data-testid="qf-comments-input"
              value={form.comments}
              onChange={update("comments")}
              placeholder="Delivery windows, special handling, frequency, etc."
              rows={4}
              className="rounded-none border-gray-300"
            />
          </Field>
        </div>
      </div>

      <Button
        type="submit"
        data-testid="contact-form-submit"
        disabled={submitting}
        className="mt-7 w-full rounded-none bg-[#D90429] py-6 text-base font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#b00321]"
      >
        {submitting ? (
          <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sending…
          </>
        ) : (
          "Request a Quote"
        )}
      </Button>
      <p className="mt-4 text-center text-xs text-gray-500">
        Prefer to talk? Call{" "}
        <a href="tel:9313259599" className="font-semibold text-[#0B2447]">
          931-325-9599
        </a>
      </p>
    </form>
  );
}

function Field({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <Label className="mb-2 block text-xs font-bold uppercase tracking-[0.15em] text-gray-500">
        {label}
      </Label>
      {children}
    </div>
  );
}
